import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { safeFocus, isIOSWebView } from "../lib/ios_focus_guard"

const log = createLogger('ScrollFocus')

// Scroll Focus Constants - extracted from magic numbers
const KEYBOARD_APPEAR_DELAY_MS = 300
const IOS_KEYBOARD_APPEAR_DELAY_MS = 500  // iOS native event ~260-350ms + margin
const SCROLL_ANIMATION_DURATION_MS = 350
const PRECHECK_MIN_KEYBOARD_HEIGHT = 100    // Min height for pre-flight check (px)
const KEYBOARD_POSITION_RATIO = 0.33        // 1/3 of available space above keyboard
const FALLBACK_TARGET_POSITION = 0.3        // 30% from top (fallback)
const SCREEN_MIDDLE_THRESHOLD = 0.5         // Only scroll if input below 50%
const FALLBACK_HEADER_HEIGHT_PX = 0
const HEADER_OFFSET_BUFFER_PX = 20
const SCROLL_ADJUSTMENT_BUFFER_PX = 40

/**
 * Scroll Focus Controller - Global keyboard-aware scroll management
 *
 * Features:
 * - Scrolls focused input above mobile keyboard (visualViewport API)
 * - Scrolls to first validation error on page load/form submit
 * - Respects sticky headers and safe areas
 * - Works with iOS Safari and Android WebView (Turbo Native)
 *
 * Usage:
 *   <form data-controller="scroll-focus">
 *     <input type="text" />
 *   </form>
 *
 * Or with form-validation:
 *   <form data-controller="product-form scroll-focus">
 *
 * Listens to form-validation#error:shown event for validation scroll
 */
export default class extends Controller {
  static values = {
    disabled: { type: Boolean, default: false }, // Disable scroll-focus behavior
    keyboardThreshold: { type: Number, default: 150 }, // Min height to detect keyboard
    scrollDismiss: { type: Boolean, default: false }  // Opt-in scroll dismiss (Android adjustNothing)
  }

  connect() {
    if (this.disabledValue) return

    // State machine for keyboard lifecycle
    // States: 'hidden' | 'showing' | 'shown' | 'hiding'
    this.keyboardState = 'hidden'
    this.pendingScrollInput = null  // Focus intent storage (survives blur/focus cycles)
    this.lastScrollTimestamp = 0    // Prevent duplicate scrolls within 500ms

    // Legacy state (kept for compatibility)
    this.viewportHeight = window.innerHeight
    this.keyboardVisible = false
    this.activeInput = null
    this.scrollTimeout = null
    this.androidKeyboardHeight = 0
    this.iosKeyboardHeight = 0
    this.keyboardHeight = 0  // Unified keyboard height

    // Keyboard height tracking for pre-flight checks and retry logic
    this.keyboardHeightCache = 0      // Last known keyboard height (persists when keyboard hidden)
    this.needsRetryScroll = false     // Flag for retry when keyboard event arrives late

    // Track programmatic scrolls to prevent dismiss
    // Prevents setupScrollDismiss from blur()-ing during our own scroll animations
    this.programmaticScroll = false

    // Setup Android native keyboard listener (adjustNothing 모드 지원)
    this.setupAndroidKeyboardListener()

    // Setup iOS native keyboard listener (WKWebView 지원)
    this.setupIOSKeyboardListener()

    // Setup viewport listener for keyboard detection (iOS/Web)
    this.setupViewportListener()

    // Setup input focus handlers via event delegation
    this.setupInputListeners()

    // Restore pending scroll from previous navigation (Turbo Drive)
    this.restorePendingScroll()

    // Scroll to first error on page load (server-side validation)
    this.scrollToFirstError()
    // A same-URL error re-render morphs this form in place (no reconnect), so run it again after each morph.
    this.onMorph = () => this.scrollToFirstError()
    document.addEventListener('turbo:morph', this.onMorph)

    // Setup scroll dismiss if enabled (opt-in for product/ad forms)
    // iOS: 네이티브 keyboardDismissMode (.onDrag) + tap gesture 사용 (Toas2tWebView.swift)
    //      JS scroll dismiss는 네이티브 레이아웃 조정으로 인한 false blur 문제로 제외
    // Android: JS scroll dismiss via blur() → native IME hide → keyboardChange event
    if (this.scrollDismissValue && !isIOSWebView()) {
      this.setupScrollDismiss()
    }

    log.info('ScrollFocus connected', {
      hasViewport: !!window.visualViewport,
      scrollDismiss: this.scrollDismissValue,
      keyboardState: this.keyboardState
    })
  }

  disconnect() {
    document.removeEventListener('turbo:morph', this.onMorph)
    // Save pending scroll intent for Turbo Drive navigation
    this.savePendingScroll()

    this.cleanupAndroidKeyboardListener()
    this.cleanupIOSKeyboardListener()
    this.cleanupViewportListener()
    this.cleanupInputListeners()
    clearTimeout(this.scrollTimeout)
    clearTimeout(this.iosResetTimeout)

    // Clear programmatic scroll flag on disconnect
    // Prevents flag from being stuck if controller disconnects during scroll
    // (e.g., Turbo navigation, page refresh, component unmount)
    this.programmaticScroll = false

    if (this.scrollDismissHandler) {
      window.removeEventListener('scroll', this.scrollDismissHandler)
    }

    // Touch event cleanup (Phase 3)
    if (this.touchStartHandler) {
      window.removeEventListener('touchstart', this.touchStartHandler)
      window.removeEventListener('touchmove', this.touchMoveHandler)
      window.removeEventListener('touchend', this.touchEndHandler)
    }
  }

  // ============================================================
  // Android Native Keyboard Detection
  // ============================================================

  setupAndroidKeyboardListener() {
    this.handleAndroidKeyboard = this.onAndroidKeyboardChange.bind(this)
    window.addEventListener('android:keyboardChange', this.handleAndroidKeyboard)
  }

  cleanupAndroidKeyboardListener() {
    if (this.handleAndroidKeyboard) {
      window.removeEventListener('android:keyboardChange', this.handleAndroidKeyboard)
    }
  }

  /**
   * Android Native 키보드 이벤트 핸들러
   * Toas2tWebView.kt에서 IME 높이 변경 시 dispatch됨
   */
  onAndroidKeyboardChange(event) {
    const { height, visible } = event.detail
    this.androidKeyboardHeight = height  // Keep for compatibility

    log.debug('Android keyboard event', { height, visible, activeInput: this.activeInput?.name })

    // Delegate to unified handler
    this.handleKeyboardChange('android', height, visible)
  }

  // ============================================================
  // iOS Native Keyboard Detection
  // ============================================================

  setupIOSKeyboardListener() {
    this.handleIOSKeyboard = this.onIOSKeyboardChange.bind(this)
    window.addEventListener('ios:keyboardChange', this.handleIOSKeyboard)
  }

  cleanupIOSKeyboardListener() {
    if (this.handleIOSKeyboard) {
      window.removeEventListener('ios:keyboardChange', this.handleIOSKeyboard)
    }
  }

  onIOSKeyboardChange(event) {
    const { height, visible } = event.detail
    this.iosKeyboardHeight = height

    // hide 이벤트 정상 수신 시 safety reset 취소
    if (!visible) clearTimeout(this.iosResetTimeout)

    log.debug('iOS keyboard event', { height, visible, activeInput: this.activeInput?.name })
    this.handleKeyboardChange('ios', height, visible)
  }

  // ============================================================
  // Unified Keyboard State Machine
  // ============================================================

  /**
   * Unified keyboard state change handler
   * Coordinates Android/iOS native and visualViewport events to prevent duplicate scrolls
   *
   * @param {string} source - 'android' | 'ios' | 'viewport'
   * @param {number} height - Keyboard height in CSS pixels
   * @param {boolean} visible - Whether keyboard is visible
   */
  handleKeyboardChange(source, height, visible) {
    // ✅ Cache keyboard height for pre-flight checks and retry logic
    this.keyboardHeightCache = height

    const prevState = this.keyboardState
    const now = Date.now()

    // Update unified state
    this.keyboardState = visible && height > 0 ? 'shown' : 'hidden'
    this.keyboardHeight = height
    this.keyboardVisible = visible  // Legacy compatibility

    // Only scroll on HIDDEN → SHOWN transition (prevents duplicate scrolls)
    if (prevState !== 'shown' && this.keyboardState === 'shown') {
      // ✅ Case 1a: Normal flow - keyboard event arrived BEFORE timeout
      if (this.pendingScrollInput && now - this.lastScrollTimestamp > 500) {
        // Cancel fallback timeout (keyboard event arrived first)
        clearTimeout(this.scrollTimeout)

        log.debug(`Keyboard appeared (${source}), scrolling to pending input`, {
          height,
          input: this.pendingScrollInput.name || this.pendingScrollInput.id,
          prevState,
          newState: this.keyboardState
        })

        this.scrollInputAboveKeyboard(this.pendingScrollInput, height)
        this.lastScrollTimestamp = now
        this.pendingScrollInput = null
        this.needsRetryScroll = false  // ✅ Clear both flags
      }
      // ✅ Case 1b: Retry flow - timeout fired first, now keyboard event arrived late
      else if (this.needsRetryScroll && this.activeInput && now - this.lastScrollTimestamp > 500) {
        log.debug(`Keyboard appeared late (${source}), retrying scroll with correct height`, {
          height,
          input: this.activeInput.name || this.activeInput.id,
          prevState,
          newState: this.keyboardState
        })

        // Scroll again with correct keyboard height
        this.scrollInputAboveKeyboard(this.activeInput, height)
        this.lastScrollTimestamp = now
        this.needsRetryScroll = false  // ✅ Clear retry flag
      }
    }

    log.debug(`Keyboard ${prevState} → ${this.keyboardState}`, {
      source,
      height,
      visible,
      cachedHeight: this.keyboardHeightCache
    })
  }

  // ============================================================
  // Viewport (Keyboard) Detection - visualViewport API (iOS/Web)
  // ============================================================

  setupViewportListener() {
    if (!window.visualViewport) {
      log.warn('visualViewport not supported, using fallback mode')
      return
    }

    this.handleViewportResize = this.onViewportResize.bind(this)
    window.visualViewport.addEventListener('resize', this.handleViewportResize)
  }

  cleanupViewportListener() {
    if (window.visualViewport && this.handleViewportResize) {
      window.visualViewport.removeEventListener('resize', this.handleViewportResize)
    }
  }

  /**
   * Visual Viewport resize 핸들러 (iOS/Web fallback)
   * ✅ CRITICAL: Android native event has priority over visualViewport
   * In adjustNothing mode, visualViewport.height may not change when keyboard appears
   */
  onViewportResize() {
    // ✅ Skip if android native event already handled keyboard
    // Prevents double events in adjustNothing mode
    if (this.androidKeyboardHeight > 0 || this.iosKeyboardHeight > 0) {
      log.debug('visualViewport resize ignored - native event has priority', {
        androidKeyboardHeight: this.androidKeyboardHeight,
        iosKeyboardHeight: this.iosKeyboardHeight
      })
      return
    }

    const viewport = window.visualViewport
    if (!viewport) return

    const heightDelta = window.innerHeight - viewport.height
    const visible = heightDelta > this.keyboardThresholdValue

    // iOS 26 bug workaround: 키보드 숨김 후에도 heightDelta > 0인 경우 처리
    // document.activeElement가 input이 아니면 키보드가 실제로 숨겨진 것
    // https://developer.apple.com/forums/thread/800125
    const isInputFocused = document.activeElement?.matches('input, textarea, [contenteditable]')
    const actualVisible = visible && isInputFocused

    // iOS 26 stale viewport 감지 로깅 (디버깅용)
    if (visible && !isInputFocused && heightDelta > 20) {
      log.debug('iOS 26 stale viewport detected - ignoring spurious keyboard event', {
        heightDelta,
        isInputFocused,
        activeElement: document.activeElement?.tagName
      })
    }

    // Delegate to unified handler (only if no android native event)
    this.handleKeyboardChange('viewport', heightDelta, actualVisible)
  }

  // ============================================================
  // Input Focus Handling - Event Delegation
  // ============================================================

  setupInputListeners() {
    this.handleFocus = this.onInputFocus.bind(this)
    this.handleBlur = this.onInputBlur.bind(this)
    this.handleValidationError = this.onValidationError.bind(this)

    this.element.addEventListener('focusin', this.handleFocus)
    this.element.addEventListener('focusout', this.handleBlur)
    this.element.addEventListener('form-validation:error:shown', this.handleValidationError)
  }

  cleanupInputListeners() {
    this.element.removeEventListener('focusin', this.handleFocus)
    this.element.removeEventListener('focusout', this.handleBlur)
    this.element.removeEventListener('form-validation:error:shown', this.handleValidationError)
  }

  /**
   * Check if keyboard is already visible with sufficient height for immediate scroll
   * @returns {boolean} True if should scroll immediately without waiting
   */
  shouldScrollImmediately() {
    return this.keyboardState === 'shown' &&
           this.keyboardHeight > PRECHECK_MIN_KEYBOARD_HEIGHT
  }

  /**
   * Scroll immediately when keyboard is already visible (pre-flight optimization)
   * @param {HTMLElement} input - The focused input element
   */
  scrollImmediately(input) {
    log.debug('Pre-flight: keyboard already visible, scrolling immediately', {
      input: input.name || input.id,
      keyboardHeight: this.keyboardHeight
    })

    this.scrollInputAboveKeyboard(input, this.keyboardHeight)
    this.pendingScrollInput = null
    this.needsRetryScroll = false
  }

  /**
   * Schedule delayed scroll with retry logic for late keyboard events
   * @param {HTMLElement} input - The focused input element
   */
  scheduleDelayedScroll(input) {
    // ✅ IMPROVED: Faster timeout (600ms → 300ms) with retry logic
    // - Keyboard event (T=100-300ms): handleKeyboardChange() scrolls → clears pendingScrollInput
    // - No keyboard event: Timeout (T=300ms) scrolls with cache → marks for retry if needed
    // - Late keyboard event: handleKeyboardChange() retries with correct height
    // iOS: 500ms timeout to avoid racing with native event (~260-350ms delivery)
    const delay = isIOSWebView() ? IOS_KEYBOARD_APPEAR_DELAY_MS : KEYBOARD_APPEAR_DELAY_MS

    this.scrollTimeout = setTimeout(() => {
      if (this.pendingScrollInput === input) {
        const cachedHeight = this.keyboardHeightCache || 0

        if (cachedHeight > PRECHECK_MIN_KEYBOARD_HEIGHT) {
          // Use cached height (keyboard event might have arrived but not processed yet)
          log.debug('Timeout: scrolling with cached keyboard height', {
            input: input.name || input.id,
            cachedHeight
          })

          this.scrollInputAboveKeyboard(input, cachedHeight)
          this.lastScrollTimestamp = Date.now()  // Always scrolls → update timestamp
          this.needsRetryScroll = false  // Don't retry if we have good cache
        } else {
          // No keyboard height available, use fallback scroll
          log.debug('Timeout: no keyboard height, using fallback scroll', {
            input: input.name || input.id,
            keyboardState: this.keyboardState
          })

          const didScroll = this.scrollInputIntoView(input)
          if (didScroll) {
            this.lastScrollTimestamp = Date.now()  // Only update if actually scrolled
          }
          this.needsRetryScroll = true  // ✅ Mark for retry when keyboard arrives
        }

        this.pendingScrollInput = null  // ✅ Clear pending (retry uses needsRetryScroll instead)
      }
    }, delay)

    log.debug('Input focused, waiting for keyboard event or timeout', {
      input: input.name || input.id,
      keyboardState: this.keyboardState,
      timeout: `${delay}ms`,
      platform: isIOSWebView() ? 'ios' : 'default'
    })
  }

  onInputFocus(event) {
    const input = event.target
    if (!this.isScrollableInput(input)) return

    // Skip inputs inside bottom sheets or modals
    if (this.isInsideOverlay(input)) return

    this.activeInput = input
    this.pendingScrollInput = input  // Store scroll intent

    // Clear iOS safety reset from previous blur (blur→focus transition)
    clearTimeout(this.iosResetTimeout)

    // Clear any existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
      this.scrollTimeout = null
    }

    // ✅ PRE-FLIGHT CHECK: If keyboard already visible, scroll immediately
    if (this.shouldScrollImmediately()) {
      this.scrollImmediately(input)
      return
    }

    // ✅ Schedule delayed scroll with retry logic
    this.scheduleDelayedScroll(input)
  }

  onInputBlur(event) {
    clearTimeout(this.scrollTimeout)
    this.activeInput = null
    // Don't clear pendingScrollInput - validation might refocus immediately
    // It will be cleared by handleKeyboardChange or timeout

    // iOS hide 이벤트 누락 안전장치: blur 후 1초 내 hide 미수신 시 강제 리셋
    if (this.iosKeyboardHeight > 0) {
      this.iosResetTimeout = setTimeout(() => {
        if (this.iosKeyboardHeight > 0 && !this.activeInput) {
          log.debug('iOS keyboard safety reset - hide event not received')
          this.iosKeyboardHeight = 0
        }
      }, 1000)
    }
  }

  onValidationError(event) {
    const { element, fieldName } = event.detail || {}
    const input = element || this.element.querySelector(`[name*="${fieldName}"]`)

    if (!input) return

    // Cancel pending keyboard scroll (we're handling it now)
    this.pendingScrollInput = null
    clearTimeout(this.scrollTimeout)

    // Choose scroll method based on keyboard state
    if (this.keyboardState === 'shown' && this.keyboardHeight > 0) {
      // Keyboard visible: scroll above keyboard (20% from top)
      log.debug('Validation error with keyboard visible', {
        input: input.name || input.id,
        keyboardHeight: this.keyboardHeight
      })
      this.scrollInputAboveKeyboard(input, this.keyboardHeight)
    } else {
      // Keyboard hidden: center element
      log.debug('Validation error with keyboard hidden', {
        input: input.name || input.id
      })
      this.scrollToElement(input)
    }

    // Focus after scroll completes (safeFocus handles iOS keyboard timing)
    safeFocus(input, { preventScroll: true })
  }

  /**
   * Check if element is a scrollable input type
   */
  isScrollableInput(element) {
    const tagName = element.tagName?.toLowerCase()
    const scrollableTypes = ['text', 'email', 'password', 'number', 'tel', 'url', 'search']

    return (
      tagName === 'textarea' ||
      (tagName === 'input' && scrollableTypes.includes(element.type)) ||
      element.isContentEditable
    )
  }

  /**
   * Check if input is inside a fixed overlay (bottom sheet, modal)
   * These have their own scroll context
   */
  isInsideOverlay(element) {
    return element.closest('[data-controller*="bottom-sheet"], [data-controller*="modal"], .fixed.inset-0')
  }

  // ============================================================
  // Scroll Dismiss (Android adjustNothing behavior)
  // ============================================================

  /**
   * Dismiss keyboard when user scrolls (Android adjustNothing behavior)
   *
   * Uses touch events for immediate response (<100ms) instead of scroll events.
   * Touch events fire at touch start, providing instant feedback compared to scroll
   * events which wait for scroll accumulation.
   *
   * Why blur is needed: Android's adjustNothing mode doesn't auto-dismiss keyboard
   * on scroll like iOS. We must explicitly blur() the focused input to close IME.
   *
   * When keyboard is dismissed (height: 0), MainActivity dispatches:
   *   android:keyboardChange { height: 0, visible: false }
   * This triggers keyboard-offset_controller to return footer to original position.
   *
   * Excluded: Overlays (modals, bottom sheets - they manage their own keyboard)
   * Note: Chat room can optionally enable scroll-dismiss via data-scroll-focus-scroll-dismiss-value="true"
   */
  setupScrollDismiss() {
    let scrollStartY = null
    let touchStartY = null

    // Touch-based detection (mobile-first, <100ms response)
    this.touchStartHandler = (e) => {
      touchStartY = e.touches[0].clientY
      scrollStartY = window.scrollY
    }

    this.touchMoveHandler = (e) => {
      if (touchStartY === null || scrollStartY === null) return

      const touchDelta = Math.abs(e.touches[0].clientY - touchStartY)
      const scrollDelta = Math.abs(window.scrollY - scrollStartY)

      // Dismiss if: touch moved >10px OR scroll changed >10px (reduced from 20px)
      if (touchDelta > 10 || scrollDelta > 10) {
        // Skip blur if programmatic scroll is in progress
        if (this.programmaticScroll) {
          log.debug('Touch scroll detected but skipping blur (programmatic scroll)', {
            touchDelta,
            scrollDelta
          })
          // Reset tracking so next user scroll is detected properly
          touchStartY = null
          scrollStartY = null
          return
        }

        const activeElement = document.activeElement

        if (activeElement &&
            (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') &&
            !this.isInsideOverlay(activeElement)) {
          // CRITICAL: blur() triggers IME hide → MainActivity detects → dispatches height: 0
          activeElement.blur()
          log.debug('Keyboard dismissed by touch scroll', {
            touchDelta,
            scrollDelta
          })
        }

        // Reset tracking to prevent multiple blur() calls
        touchStartY = null
        scrollStartY = null
      }
    }

    this.touchEndHandler = () => {
      // Reset on touch end
      touchStartY = null
      scrollStartY = null
    }

    // Register touch events (passive for performance)
    window.addEventListener('touchstart', this.touchStartHandler, { passive: true })
    window.addEventListener('touchmove', this.touchMoveHandler, { passive: true })
    window.addEventListener('touchend', this.touchEndHandler, { passive: true })

    // Fallback: scroll event for mouse/trackpad (desktop testing)
    let lastScrollY = window.scrollY
    this.scrollDismissHandler = () => {
      const scrollDelta = Math.abs(window.scrollY - lastScrollY)

      // Reduced threshold from 20px to 10px
      if (scrollDelta > 10) {
        // Skip blur if programmatic scroll is in progress
        if (this.programmaticScroll) {
          log.debug('Scroll detected but skipping blur (programmatic scroll)', {
            scrollDelta
          })
          lastScrollY = window.scrollY  // Update tracking position
          return
        }

        const activeElement = document.activeElement

        if (activeElement &&
            (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') &&
            !this.isInsideOverlay(activeElement)) {
          activeElement.blur()
          log.debug('Keyboard dismissed by scroll (fallback)', { scrollDelta })
        }
      }
      lastScrollY = window.scrollY
    }

    window.addEventListener('scroll', this.scrollDismissHandler, { passive: true })
  }

  // ============================================================
  // Scroll Logic
  // ============================================================

  /**
   * Scroll input above keyboard when keyboard is visible
   * Uses actual keyboard height and accounts for safe area bottom (navigation bar, gesture area)
   */
  scrollInputAboveKeyboard(input, keyboardHeight) {
    // Guard clause: validate inputs
    if (!input) {
      log.warn('scrollInputAboveKeyboard called with null input')
      return
    }

    if (typeof keyboardHeight !== 'number' || keyboardHeight < 0) {
      log.warn('Invalid keyboardHeight', { keyboardHeight })
      return
    }

    const rect = input.getBoundingClientRect()
    const headerOffset = this.getHeaderOffset()

    // iOS 26 bug workaround: visualViewport.height가 stale하면 window.innerHeight 사용
    // https://developer.apple.com/forums/thread/800125
    const rawViewportHeight = window.visualViewport?.height || window.innerHeight
    const viewportHeightDiff = window.innerHeight - rawViewportHeight
    // 키보드 열린 상태가 아닌데 20px+ 차이가 있으면 stale viewport로 판단
    const isStaleViewport = keyboardHeight === 0 && viewportHeightDiff > 20
    const viewportHeight = isStaleViewport ? window.innerHeight : rawViewportHeight

    // Calculate effective keyboard height (subtract safe area bottom)
    const safeAreaBottom = this.getSafeAreaBottom()
    const effectiveKeyboardHeight = Math.max(0, keyboardHeight - safeAreaBottom)

    // Check if input will be hidden by keyboard
    const inputBottom = rect.bottom
    const visibleBottom = viewportHeight - effectiveKeyboardHeight

    // Scroll only if input is actually hidden by keyboard
    if (inputBottom > visibleBottom) {
      // Calculate target position: 1/3 of available space (above keyboard and below header)
      const availableHeight = viewportHeight - effectiveKeyboardHeight - headerOffset
      const idealTarget = availableHeight * KEYBOARD_POSITION_RATIO
      const minTarget = headerOffset + HEADER_OFFSET_BUFFER_PX  // Just below header

      const targetPosition = Math.max(minTarget, idealTarget)
      const scrollAmount = rect.top - targetPosition

      // Set flag BEFORE programmatic scroll to prevent blur during animation
      this.programmaticScroll = true

      window.scrollBy({
        top: scrollAmount,
        behavior: 'smooth'
      })

      // Clear flag after animation (350ms covers browser smooth scroll duration 100-300ms)
      setTimeout(() => {
        this.programmaticScroll = false
      }, SCROLL_ANIMATION_DURATION_MS)

      log.debug('Scrolled input above keyboard', {
        input: input.name || input.id,
        keyboardHeight,
        effectiveKeyboardHeight,
        safeAreaBottom,
        headerOffset,
        targetPosition: Math.round(targetPosition),
        scrollAmount: Math.round(scrollAmount),
        programmaticScroll: true
      })
    }
  }

  /**
   * Scroll input into view (fallback for browsers without visualViewport)
   */
  scrollInputIntoView(input) {
    // Guard clause: validate input
    if (!input) {
      log.warn('scrollInputIntoView called with null input')
      return false
    }

    const rect = input.getBoundingClientRect()
    const headerOffset = this.getHeaderOffset()

    // Target position: 30% from top
    const targetPosition = window.innerHeight * FALLBACK_TARGET_POSITION

    // Only scroll if input is in bottom half of screen
    if (rect.top > window.innerHeight * SCREEN_MIDDLE_THRESHOLD) {
      const scrollAmount = rect.top - targetPosition - headerOffset

      // Set flag BEFORE programmatic scroll
      this.programmaticScroll = true

      window.scrollBy({
        top: scrollAmount,
        behavior: 'smooth'
      })

      // Clear flag after animation (350ms covers browser smooth scroll duration 100-300ms)
      setTimeout(() => {
        this.programmaticScroll = false
      }, SCROLL_ANIMATION_DURATION_MS)

      log.debug('Scrolled input into view', {
        input: input.name || input.id,
        scrollAmount: Math.round(scrollAmount),
        programmaticScroll: true
      })

      return true
    }

    return false
  }

  // ============================================================
  // Validation Error Scrolling
  // ============================================================

  /**
   * Scroll to first validation error on page load
   * Handles server-side rendered validation errors
   */
  scrollToFirstError() {
    // Wait for DOM to be fully rendered
    requestAnimationFrame(() => {
      // Match real error CONTAINERS and client-invalidated form FIELDS only:
      //  - [role="alert"] / .bg-red-50 → server-rendered error summaries (ToastComponent :error,
      //    devise/_error_messages, _error_notification, listing/ad new) — all use role=alert and/or bg-red-50.
      //  - input/textarea/select.border-red-500 → fields flagged invalid by form_validation_controller.
      // Red notices that are not errors (the listing step 7 consequence strip) opt out with data-scroll-focus-ignore.
      // NOT bare red-text utilities (.text-red-500/600) or any-element .border-red-500: those style logout/
      // delete links, prices, the password-strength meter, etc. — not errors. Matching them caused a false
      // "scroll to first error" that jumped the page to center on load on error-free pages (e.g. /profile).
      const firstError = this.element.querySelector(
        '[role="alert"]:not(.hidden), .bg-red-50:not(.hidden):not([data-scroll-focus-ignore]), input.border-red-500, textarea.border-red-500, select.border-red-500'
      )

      if (!firstError) return

      // Find associated input field
      const input = this.findErrorInput(firstError)

      if (input) {
        this.scrollToElement(input)
        // Focus after scroll animation completes (safeFocus handles iOS keyboard timing)
        setTimeout(() => {
          safeFocus(input, { preventScroll: true })
        }, SCROLL_ANIMATION_DURATION_MS)

        log.info('Scrolled to first validation error', {
          input: input.name || input.id
        })
      } else {
        this.scrollToElement(firstError)
      }
    })
  }

  /**
   * Find the input field associated with an error element
   */
  findErrorInput(errorElement) {
    // Strategy 1: aria-describedby link
    const errorId = errorElement.id
    if (errorId) {
      const input = this.element.querySelector(`[aria-describedby*="${errorId}"]`)
      if (input) return input
    }

    // Strategy 2: data-field-name attribute (form_validation_controller pattern)
    const fieldName = errorElement.dataset.fieldName
    if (fieldName) {
      const input = this.element.querySelector(`[name="${fieldName}"], [name*="${fieldName}"]`)
      if (input) return input
    }

    // Strategy 3: Error element has red border (is the input itself)
    if (errorElement.matches('input, textarea, select')) {
      return errorElement
    }

    // Strategy 4: Previous sibling or parent container input
    const container = errorElement.closest('.mb-5, .space-y-3, .mt-2, .field-group')?.parentElement
    if (container) {
      return container.querySelector('input, textarea, select')
    }

    return null
  }

  /**
   * Scroll to element with header offset compensation
   */
  scrollToElement(element) {
    const headerOffset = this.getHeaderOffset()

    // Set flag BEFORE programmatic scroll
    this.programmaticScroll = true

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })

    // Adjust for sticky header after scrollIntoView completes
    setTimeout(() => {
      const rect = element.getBoundingClientRect()
      if (rect.top < headerOffset + HEADER_OFFSET_BUFFER_PX) {
        window.scrollBy({
          top: rect.top - headerOffset - SCROLL_ADJUSTMENT_BUFFER_PX,
          behavior: 'smooth'
        })
      }
    }, SCROLL_ANIMATION_DURATION_MS)

    // Clear flag after both scroll animations complete (350ms + 350ms = 700ms)
    setTimeout(() => {
      this.programmaticScroll = false
    }, SCROLL_ANIMATION_DURATION_MS * 2)
  }

  // ============================================================
  // Utilities
  // ============================================================

  /**
   * Get sticky/fixed header height for scroll offset calculation
   */
  getHeaderOffset() {
    const header = document.querySelector(
      'header.sticky, header.fixed, [data-sticky-header], div.sticky.top-0'
    )
    return header?.offsetHeight || FALLBACK_HEADER_HEIGHT_PX
  }

  /**
   * Get safe area bottom (navigation bar, gesture area) for keyboard offset calculation
   * Matches keyboard-offset-controller.js implementation for consistent effective keyboard height
   */
  getSafeAreaBottom() {
    const style = getComputedStyle(document.documentElement)
    const safeAreaStr = style.getPropertyValue('--android-safe-area-bottom') ||
                        style.getPropertyValue('--safe-area-bottom') ||
                        '0px'
    return parseInt(safeAreaStr, 10) || 0
  }

  // ============================================================
  // State Persistence (Turbo Drive Support)
  // ============================================================

  /**
   * Save pending scroll intent to sessionStorage for Turbo Drive navigation
   * Called on disconnect() to preserve state across page transitions
   */
  savePendingScroll() {
    if (this.pendingScrollInput) {
      const data = {
        selector: `[name="${this.pendingScrollInput.name}"]`,
        id: this.pendingScrollInput.id,
        timestamp: Date.now()
      }

      try {
        sessionStorage.setItem('toas2t_scroll_focus_pending', JSON.stringify(data))
        log.debug('Saved pending scroll to sessionStorage', data)
      } catch (e) {
        log.error('Failed to save pending scroll', e)
        // Continue gracefully if sessionStorage fails (quota exceeded, privacy mode, etc.)
      }
    }
  }

  /**
   * Restore pending scroll intent from sessionStorage after Turbo Drive navigation
   * Called on connect() to restore state from previous page
   */
  restorePendingScroll() {
    const saved = sessionStorage.getItem('toas2t_scroll_focus_pending')
    if (!saved) return

    try {
      const data = JSON.parse(saved)

      // Only restore if <2 seconds old (prevents stale data after long inactivity)
      const age = Date.now() - data.timestamp
      if (age < 2000) {
        // Try to find input by name or id
        const selector = data.selector || (data.id ? `#${data.id}` : null)
        if (selector) {
          const input = this.element.querySelector(selector)
          if (input) {
            this.pendingScrollInput = input
            log.debug('Restored pending scroll from sessionStorage', {
              selector,
              age: `${age}ms`
            })
          }
        }
      } else {
        log.debug('Ignored stale pending scroll', { age: `${age}ms` })
      }

      // Clean up regardless of restore success
      sessionStorage.removeItem('toas2t_scroll_focus_pending')
    } catch (e) {
      log.error('Failed to restore pending scroll', e)
      // Clean up corrupted data
      sessionStorage.removeItem('toas2t_scroll_focus_pending')
    }
  }
}
